import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { List, ListItemIcon } from '@material-ui/core';
import { IntlShape, injectIntl } from 'react-intl';
import {
  IconWrapper,
  Item,
  StyledListItem,
  StyledListItemText,
  StyledSubListItem,
  StyledCollapse,
  Stick,
} from 'containers/AppContainer/SideBar/style';
import FeeNoteIcon from 'components/SahamIcons/FeeNoteIcon';
import HomeIcon from 'components/SahamIcons/HomeIcon';
import { translate } from 'containers/services/translate';
import messages from 'containers/AppContainer/messages';
import { history } from 'store';
import { PAGES } from 'routes';
import MissionIcon from 'components/SahamIcons/MissionIcon';
import NewSinisterIcon from 'components/SahamIcons/NewSinistersIcon';
import ArrowRightIcon from 'components/SahamIcons/ArrowRightIcon';
import SettingIcon from 'components/SahamIcons/SettingIcon';
import ArrowDownIcon from 'components/SahamIcons/ArrowDownIcon';
import userPrivileges, {
  PRIVILEGES,
} from 'containers/services/security/user-privileges';
import { requestOpenLockClaimDialog } from 'containers/AppContainer/actions';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';

const selectedItem = (paths: string[]) => {
  return paths.some(path => path === history.location.pathname);
};

export interface OpeningManagerMenuProps {
  intl: IntlShape;
  isManager?: boolean;
  pushToAndReset: Function;
  isMenuOpen: boolean;
}

const OpeningManagerMenu: FC<OpeningManagerMenuProps> = (
  props,
): JSX.Element => {
  const dispatch = useDispatch();
  const [subListOpen, setSubListOpen] = useState(false);
  const [nestedListOpen, setNestedListOpen] = useState(false);

  const toggleSubListOpen = () => {
    setSubListOpen(!subListOpen);
  };
  /* istanbul ignore next */
  const handleOpenUnlockClaimDialog = () => {
    dispatch(requestOpenLockClaimDialog(true));
  };
  /* istanbul ignore next */
  const toggleNestedListOpen = () => {
    setNestedListOpen(!nestedListOpen);
  };
  const { pushToAndReset, isMenuOpen } = props;

  return (
    <List>
      <Item>
        <StyledListItem
          open={isMenuOpen}
          button
          selected={selectedItem([PAGES.MANAGER_HOME])}
          onClick={pushToAndReset(PAGES.MANAGER_HOME)}
        >
          <ListItemIcon>
            <HomeIcon isActivated />
          </ListItemIcon>
          {isMenuOpen && (
            <StyledListItemText open={isMenuOpen}>
              <span className="menuItemText">
                {translate(messages.sideBarHomeTitle, props)}
              </span>
            </StyledListItemText>
          )}
        </StyledListItem>
      </Item>
      <Item>
        <StyledListItem
          data-testid="sub-menu"
          open={isMenuOpen}
          button
          selected={selectedItem([
            PAGES.VALIDATED_CLAIMS,
            PAGES.PENDING_CLAIMS,
            PAGES.REJECTED_CLAIMS,
          ])}
          onClick={toggleSubListOpen}
        >
          <ListItemIcon>
            <NewSinisterIcon />
          </ListItemIcon>
          {isMenuOpen && (
            <StyledListItemText open={isMenuOpen}>
              <span className="menuItemText">
                {translate(messages.newSinistersTitle, props)}
              </span>
            </StyledListItemText>
          )}
          {isMenuOpen ? (
            subListOpen ? (
              <IconWrapper>
                <ArrowDownIcon />
              </IconWrapper>
            ) : (
              <IconWrapper>
                <ArrowRightIcon />
              </IconWrapper>
            )
          ) : null}
        </StyledListItem>
      </Item>
      <StyledCollapse
        in={subListOpen && isMenuOpen}
        timeout="auto"
        unmountOnExit
      >
        <Stick />
        <List component="div" disablePadding>
          <Item>
            <StyledSubListItem
              open={isMenuOpen}
              button
              selected={selectedItem([PAGES.MANAGER_OPENED_CLAIMS])}
              onClick={pushToAndReset(PAGES.MANAGER_OPENED_CLAIMS)}
            >
              {isMenuOpen && (
                <StyledListItemText open={isMenuOpen}>
                  <span className="menuItemText">
                    {translate(messages.sideBarOpenedTitle, props)}
                  </span>
                </StyledListItemText>
              )}
            </StyledSubListItem>
          </Item>
          {!featureService.hasFeature(FEATURES.ATTRIBUTION_INBOXES_MANAGER) && (
            <Item>
              <StyledSubListItem
                open={isMenuOpen}
                button
                selected={selectedItem([PAGES.MANAGER_PENDING_CLAIMS])}
                onClick={pushToAndReset(PAGES.MANAGER_PENDING_CLAIMS)}
              >
                {isMenuOpen && (
                  <StyledListItemText open={isMenuOpen}>
                    <span className="menuItemText">
                      {translate(messages.sideBarPendingTitle, props)}
                    </span>
                  </StyledListItemText>
                )}
              </StyledSubListItem>
            </Item>
          )}
          <Item>
            <StyledSubListItem
              open={isMenuOpen}
              button
              selected={selectedItem([PAGES.MANAGER_REJECTED_CLAIMS])}
              onClick={pushToAndReset(PAGES.MANAGER_REJECTED_CLAIMS)}
            >
              {isMenuOpen && (
                <StyledListItemText open={isMenuOpen}>
                  <span className="menuItemText">
                    {translate(messages.sideBarRejectedTitle, props)}
                  </span>
                </StyledListItemText>
              )}
            </StyledSubListItem>
          </Item>
        </List>
      </StyledCollapse>
      {featureService.hasFeature(FEATURES.ATTRIBUTION_INBOXES_MANAGER) && (
        <Item>
          <StyledListItem
            open={isMenuOpen}
            button
            selected={selectedItem([PAGES.MANAGER_PENDING_TREATEMENT_CLAIMS])}
            onClick={pushToAndReset(PAGES.MANAGER_PENDING_TREATEMENT_CLAIMS)}
          >
            <ListItemIcon>
              <MissionIcon />
            </ListItemIcon>
            {isMenuOpen && (
              <StyledListItemText open={isMenuOpen}>
                <span className="menuItemText">
                  {translate(messages.pendingTreatementClaims, props)}
                </span>
              </StyledListItemText>
            )}
          </StyledListItem>
        </Item>
      )}
      <Item>
        <StyledListItem
          open={isMenuOpen}
          button
          selected={selectedItem([PAGES.MANAGER_FEE_NOTE])}
          onClick={pushToAndReset(PAGES.MANAGER_FEE_NOTE)}
        >
          <ListItemIcon>
            <FeeNoteIcon />
          </ListItemIcon>
          {isMenuOpen && (
            <>
              <StyledListItemText open={isMenuOpen}>
                <span className="menuItemText">
                  {translate(messages.sideBarFeeNote, props)}
                </span>
              </StyledListItemText>
            </>
          )}
        </StyledListItem>
      </Item>
      {userPrivileges.userHasPrivilege(PRIVILEGES.ISMANAGER) &&
        featureService.hasFeature(FEATURES.ADMINISTRATION_MANAGER) && (
          <>
            <Item>
              <StyledListItem
                data-testid="sub-menu"
                open={nestedListOpen}
                button
                onClick={toggleNestedListOpen}
              >
                <ListItemIcon>
                  <SettingIcon />
                </ListItemIcon>

                <StyledListItemText open={true}>
                  <span className="menuItemText">
                    {translate(messages.sideBarAdministration, props)}
                  </span>
                </StyledListItemText>

                {nestedListOpen ? (
                  <IconWrapper>
                    <ArrowDownIcon />
                  </IconWrapper>
                ) : (
                  <IconWrapper>
                    <ArrowRightIcon />
                  </IconWrapper>
                )}
              </StyledListItem>
            </Item>

            <StyledCollapse in={nestedListOpen} timeout="auto" unmountOnExit>
              <Stick />
              <List component="div" disablePadding>
                {featureService.hasFeature(FEATURES.MANAGER_LOCK_CLAIM) && (
                  <Item>
                    <StyledSubListItem
                      open={nestedListOpen}
                      button
                      onClick={handleOpenUnlockClaimDialog}
                    >
                      {nestedListOpen && (
                        <StyledListItemText open={nestedListOpen}>
                          <span className="menuItemText">
                            {translate(messages.unlockClaim, props)}
                          </span>
                        </StyledListItemText>
                      )}
                    </StyledSubListItem>
                  </Item>
                )}
                {featureService.hasFeature(
                  FEATURES.ATTRIBUTION_INBOXES_MANAGER,
                ) && (
                  <Item>
                    <StyledSubListItem
                      open={nestedListOpen}
                      button
                      onClick={pushToAndReset(
                        PAGES.ATTRIBUTION_OPENING_MANAGER_PAGE,
                      )}
                    >
                      {nestedListOpen && (
                        <StyledListItemText open={nestedListOpen}>
                          <span className="menuItemText">
                            {translate(messages.attributionOpeningTitle, props)}
                          </span>
                        </StyledListItemText>
                      )}
                    </StyledSubListItem>
                  </Item>
                )}
                {featureService.hasFeature(
                  FEATURES.ATTRIBUTION_INBOXES_MANAGER,
                ) && (
                  <Item>
                    <StyledSubListItem
                      open={isMenuOpen}
                      button
                      onClick={pushToAndReset(
                        PAGES.ATTRIBUTION_JUDICIARY_MANAGER_PAGE,
                      )}
                    >
                      {isMenuOpen && (
                        <StyledListItemText open={isMenuOpen}>
                          <span className="menuItemText">
                            {translate(
                              messages.attributionJudiciaryTitle,
                              props,
                            )}
                          </span>
                        </StyledListItemText>
                      )}
                    </StyledSubListItem>
                  </Item>
                )}
                {featureService.hasFeature(
                  FEATURES.ATTRIBUTION_INBOXES_TRANSACTION_MANAGER,
                ) && (
                  <Item>
                    <StyledSubListItem
                      open={isMenuOpen}
                      button
                      onClick={pushToAndReset(
                        PAGES.ATTRIBUTION_TRANSACTION_MANAGER_PAGE,
                      )}
                    >
                      {isMenuOpen && (
                        <StyledListItemText open={isMenuOpen}>
                          <span className="menuItemText">
                            {translate(
                              messages.attributionTransactionTitle,
                              props,
                            )}
                          </span>
                        </StyledListItemText>
                      )}
                    </StyledSubListItem>
                  </Item>
                )}
                {featureService.hasFeature(FEATURES.PLATFORM_MANAGER) && (
                  <Item>
                    <StyledSubListItem
                      open={isMenuOpen}
                      button
                      onClick={() => {}} //to-update
                    >
                      {isMenuOpen && (
                        <StyledListItemText open={isMenuOpen}>
                          <span className="menuItemText">
                            {translate(messages.platformManagerTitle, props)}
                          </span>
                        </StyledListItemText>
                      )}
                    </StyledSubListItem>
                  </Item>
                )}
                {featureService.hasFeature(
                  FEATURES.PLATFORM_MANAGER_INTERVENANT,
                ) && (
                  <Item>
                    <StyledSubListItem
                      open={isMenuOpen}
                      button
                      onClick={pushToAndReset(
                        PAGES.PLATFORM_MANAGER_MANAGEMENT_INTERVENANT,
                      )}
                    >
                      {isMenuOpen && (
                        <StyledListItemText open={isMenuOpen}>
                          <span className="menuItemText">
                            {translate(
                              messages.platformManagerIntervenantTitle,
                              props,
                            )}
                          </span>
                        </StyledListItemText>
                      )}
                    </StyledSubListItem>
                  </Item>
                )}
              </List>
            </StyledCollapse>
          </>
        )}
    </List>
  );
};

export default injectIntl(OpeningManagerMenu);
